import { addDays } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../containers/ApiContainer";
import { PlacementService } from "../../../services/PlacementService";
import { TenantService } from "../../../services/TenantService";
import { TripService } from "../../../services/TripService";
import {
  Aircraft,
  CsrStatus,
  PlacementWithLocation,
  TripWithTenant,
} from "../../../types";
import { encodeTimeToUrl } from "../../../utils/urlUtils";
import { TripDetailsModalPresenter } from "./TripDetailsModalPresenter";

interface TripDetailsModalProps {
  open: boolean;
  trip: TripWithTenant | null;
  trips: TripWithTenant[];
  onClose: () => void;
  onUpdate?: (
    tripId: string,
    updates: Partial<TripWithTenant>
  ) => Promise<void>;
  onNavigate?: (tripId: string) => void;
  onTripChanged?: () => void;
  currentTimestamp?: Date;
}

export const TripDetailsModal: React.FC<TripDetailsModalProps> = ({
  open,
  trip,
  trips,
  onClose,
  onUpdate,
  onNavigate,
  onTripChanged,
  currentTimestamp,
}) => {
  const { postgrest } = useApi();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [editedTrip, setEditedTrip] = useState<Partial<TripWithTenant>>({});
  const [selectedAircraft, setSelectedAircraft] = useState<Aircraft | null>(
    null
  );

  // Placement state
  const [placements, setPlacements] = useState<PlacementWithLocation[]>([]);
  const [_, setPlacementsLoading] = useState(false);

  // Services and hooks
  const tenantService = useMemo(
    () => new TenantService(postgrest),
    [postgrest]
  );

  // Location assignment dialog state
  const [assignLocationOpen, setAssignLocationOpen] = useState(false);

  // Create trip dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Handle location navigation
  const handleLocationClick = useCallback(
    (placement: PlacementWithLocation) => {
      if (!placement.hangar_id && !placement.ramp_id) return;

      const arrivalTime = encodeTimeToUrl(new Date(placement.start_time));

      if (placement.hangar_id) {
        navigate(
          `/hangars/${placement.hangar_id}/schedule?date=${arrivalTime}`
        );
      } else if (placement.ramp_id) {
        navigate(`/ramps/${placement.ramp_id}/schedule?date=${arrivalTime}`);
      }
    },
    [navigate]
  );

  // Confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  // Initialize services
  const placementService = useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );
  const tripService = useMemo(() => new TripService(postgrest), [postgrest]);

  // Get current placement location (for AssignLocationDialog)
  const currentLocation = useMemo(() => {
    if (!placements || placements.length === 0) return null;
    const currentPlacement = placements[0];
    if (currentPlacement.id === "synthetic") return null;
    return currentPlacement.hangar_id || currentPlacement.ramp_id || null;
  }, [placements]);

  // Get current location type
  const currentLocationType = useMemo(() => {
    if (!placements || placements.length === 0) return undefined;
    const currentPlacement = placements[0];
    if (currentPlacement.id === "synthetic") return undefined;
    if (currentPlacement.hangar_id) return "hangar" as const;
    if (currentPlacement.ramp_id) return "ramp" as const;
    return undefined;
  }, [placements]);

  // Reset when trip changes
  useEffect(() => {
    if (trip) {
      setEditedTrip({
        arrival_date: trip.arrival_date,
        departure_date: trip.departure_date,
        status: trip.status,
        csr_status: trip.csr_status || "requested",
        notes: trip.notes || "",
        tail_number: trip.tail_number || "",
        owner_manager: trip.owner_manager || "",
      });

      // Set current aircraft based on make and model
      if (trip.aircraft_manufacturer && trip.aircraft_model) {
        setSelectedAircraft({
          id: "",
          make: trip.aircraft_manufacturer,
          model: trip.aircraft_model,
        } as Aircraft);
      } else {
        setSelectedAircraft(null);
      }

      setIsEditing(false);
      setError(null);
    }
  }, [trip]);

  // Helper function to fetch placements (reusable)
  const fetchPlacements = async () => {
    if (!trip) return;

    setPlacementsLoading(true);
    try {
      const data = await placementService.getPlacementsWithLocation(
        trip.trip_id
      );

      // If no placements exist, create a synthetic "Unassigned" placement
      if (data.length === 0) {
        setPlacements([
          {
            id: "synthetic",
            trip_id: trip.trip_id,
            start_time: trip.arrival_date,
            end_time: trip.departure_date,
            location_name: "Unassigned",
            location_type: "transient" as const,
            created_at: "",
            updated_at: "",
            version: 1,
          },
        ]);
      } else {
        setPlacements(data);
      }
    } catch (err) {
      console.error("Failed to fetch placements:", err);
      setPlacements([
        {
          id: "synthetic",
          trip_id: trip.trip_id,
          start_time: trip.arrival_date,
          end_time: trip.departure_date,
          location_name: "Unassigned",
          location_type: "transient" as const,
          created_at: "",
          updated_at: "",
          version: 1,
        },
      ]);
    } finally {
      setPlacementsLoading(false);
    }
  };

  // Fetch placements when trip changes
  useEffect(() => {
    fetchPlacements();
  }, [trip]);

  // Find next trip chronologically (any aircraft)
  const nextTrip = useMemo(() => {
    if (!trip) return null;

    const sortedTrips = trips
      .filter((t) => t.trip_id !== trip.trip_id)
      .sort(
        (a, b) =>
          new Date(a.arrival_date).getTime() -
          new Date(b.arrival_date).getTime()
      );

    return (
      sortedTrips.find(
        (t) => new Date(t.arrival_date) > new Date(trip.departure_date)
      ) || null
    );
  }, [trip, trips]);

  // Find previous trip chronologically (any aircraft)
  const previousTrip = useMemo(() => {
    if (!trip) return null;

    const sortedTrips = trips
      .filter((t) => t.trip_id !== trip.trip_id)
      .sort(
        (a, b) =>
          new Date(b.departure_date).getTime() -
          new Date(a.departure_date).getTime()
      );

    return (
      sortedTrips.find(
        (t) => new Date(t.departure_date) < new Date(trip.arrival_date)
      ) || null
    );
  }, [trip, trips]);

  const handleSave = async () => {
    if (!trip) return;

    setLoading(true);
    setError(null);

    try {
      const tripService = new TripService(postgrest);

      // Step 1: Update trip fields (dates, status, notes, csr_status)
      const tripUpdates = {
        arrival_date: editedTrip.arrival_date,
        departure_date: editedTrip.departure_date,
        status: editedTrip.status,
        csr_status: editedTrip.csr_status,
        notes: editedTrip.notes,
      };

      // Call parent's onUpdate or use TripService directly
      if (onUpdate) {
        await onUpdate(trip.trip_id, tripUpdates);
      } else {
        await tripService.updateTrip(trip.trip_id, tripUpdates);
      }

      // Step 2: Update tenant fields if changed
      const tenantUpdates: any = {};
      let needTenantUpdate = false;

      // Check if tail number changed
      if (editedTrip.tail_number !== trip.tail_number) {
        tenantUpdates.tail_number =
          editedTrip.tail_number?.trim().toUpperCase() || null;
        needTenantUpdate = true;
      }

      // Check if owner/manager changed
      if (editedTrip.owner_manager !== trip.owner_manager) {
        tenantUpdates.owner_manager = editedTrip.owner_manager?.trim() || null;
        needTenantUpdate = true;
      }

      // Check if aircraft changed
      if (
        selectedAircraft &&
        (selectedAircraft.make !== trip.aircraft_manufacturer ||
          selectedAircraft.model !== trip.aircraft_model)
      ) {
        tenantUpdates.aircraft_id = selectedAircraft.id || null;
        needTenantUpdate = true;
      }

      // Update tenant if needed
      if (needTenantUpdate && trip.tenant_id) {
        await tenantService.updateTenant(trip.tenant_id, tenantUpdates);
      }

      // Refresh the trip data
      onTripChanged?.();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update trip");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (trip) {
      setEditedTrip({
        arrival_date: trip.arrival_date,
        departure_date: trip.departure_date,
        status: trip.status,
        csr_status: trip.csr_status || "requested",
        notes: trip.notes || "",
        tail_number: trip.tail_number || "",
        owner_manager: trip.owner_manager || "",
      });

      // Reset aircraft selection
      if (trip.aircraft_manufacturer && trip.aircraft_model) {
        setSelectedAircraft({
          id: "",
          make: trip.aircraft_manufacturer,
          model: trip.aircraft_model,
        } as Aircraft);
      } else {
        setSelectedAircraft(null);
      }
    }
    setIsEditing(false);
    setError(null);
  };

  // Handle cancelling a trip (set status to cancelled)
  // NOTE: Cancelled trips are automatically unassigned from all hangars/ramps
  // by the database trigger 'auto_unassign_cancelled_trips'
  // See: src/web/schema/V0.45__auto_unassign_cancelled_trips.sql
  const handleCancelTrip = async () => {
    if (!trip) return;

    setLoading(true);
    setError(null);
    setCancelConfirmOpen(false);

    try {
      await tripService.updateTripStatus(trip.trip_id, "cancelled");
      onTripChanged?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel trip");
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a trip
  const handleDeleteTrip = async () => {
    if (!trip) return;

    setLoading(true);
    setError(null);
    setDeleteConfirmOpen(false);

    try {
      await tripService.deleteTrip(trip.trip_id);
      onTripChanged?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete trip");
    } finally {
      setLoading(false);
    }
  };

  // Handle location assignment
  const handleLocationAssigned = async () => {
    setAssignLocationOpen(false);
    // Refresh placements to show the new location
    await fetchPlacements();
    // Notify parent that trip was changed
    onTripChanged?.();
  };

  // Handle new trip creation
  const handleTripCreated = async () => {
    setCreateDialogOpen(false);
    // Notify parent that a new trip was created
    onTripChanged?.();
  };

  // Handle CSR status change - saves immediately without requiring edit mode
  const handleCsrStatusChange = useCallback(async (newStatus: CsrStatus) => {
    if (!trip) return;

    // Update local state immediately (optimistic)
    setEditedTrip(prev => ({ ...prev, csr_status: newStatus }));

    try {
      // Persist to database
      await tripService.updateTrip(trip.trip_id, { csr_status: newStatus });
      // Notify parent of the change
      onTripChanged?.();
    } catch (err) {
      console.error("Failed to update CSR status:", err);
      setError("Failed to update status");
      // Revert on error
      setEditedTrip(prev => ({ ...prev, csr_status: trip.csr_status }));
    }
  }, [trip, tripService, onTripChanged]);

  // Calculate initial dates for new trip (avoid overlap with current trip)
  const getInitialDatesForNewTrip = () => {
    if (!trip) {
      return {
        arrivalDate: new Date(),
        departureDate: addDays(new Date(), 2),
      };
    }
    // Start new trip after current trip's departure
    const departureDate = new Date(trip.departure_date);
    const newArrivalDate = addDays(departureDate, 1);
    const newDepartureDate = addDays(newArrivalDate, 2);
    return {
      arrivalDate: newArrivalDate,
      departureDate: newDepartureDate,
    };
  };

  return (
    <TripDetailsModalPresenter
      open={open}
      trip={trip}
      onClose={onClose}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      loading={loading}
      error={error}
      setError={setError}
      editedTrip={editedTrip}
      setEditedTrip={setEditedTrip}
      selectedAircraft={selectedAircraft}
      setSelectedAircraft={setSelectedAircraft}
      placements={placements}
      currentLocation={currentLocation}
      currentLocationType={currentLocationType}
      currentTimestamp={currentTimestamp}
      previousTrip={previousTrip}
      nextTrip={nextTrip}
      onNavigate={onNavigate}
      handleSave={handleSave}
      handleCancel={handleCancel}
      handleCancelTrip={handleCancelTrip}
      handleDeleteTrip={handleDeleteTrip}
      handleLocationClick={handleLocationClick}
      handleLocationAssigned={handleLocationAssigned}
      deleteConfirmOpen={deleteConfirmOpen}
      setDeleteConfirmOpen={setDeleteConfirmOpen}
      cancelConfirmOpen={cancelConfirmOpen}
      setCancelConfirmOpen={setCancelConfirmOpen}
      assignLocationOpen={assignLocationOpen}
      setAssignLocationOpen={setAssignLocationOpen}
      createDialogOpen={createDialogOpen}
      setCreateDialogOpen={setCreateDialogOpen}
      onTripCreated={handleTripCreated}
      getInitialDatesForNewTrip={getInitialDatesForNewTrip}
      onCsrStatusChange={handleCsrStatusChange}
    />
  );
};
